import { updateOrganization } from "@app/auth";
import {
  computeCostId,
  fetchCostsByStacks,
  selectStacksByOrgAsList,
} from "@app/deploy";
import { createSupportTicket, resetSupportTicket } from "@app/deploy/support";
import { selectOrganizationSelected } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { stackDetailEnvsUrl } from "@app/routes";
import { schema } from "@app/schema";
import type { Organization } from "@app/types";
import { selectCurrentUser } from "@app/users";
import { emailValidator, existValidator } from "@app/validator";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useValidator } from "../hooks";
import {
  BannerMessages,
  Box,
  Button,
  CheckBox,
  FormGroup,
  Group,
  IconInfo,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
  ModalWrapper,
  Switch,
  TextArea,
  Tooltip,
  tokens,
} from "../shared";

const RELIABILITY_DOCS_URL =
  "https://www.aptible.com/docs/core-concepts/architecture/reliability-division";

const REQUIRED_WHEN_OWNER_NOTIFY_DISABLED =
  "Required — account owners are not automatically notified.";

const ownerNotifyEnabled = (org: Organization) =>
  org.opsAlertOwnerNotify && org.securityAlertOwnerNotify;

const validators = {
  securityEmail: (org: Organization) => {
    if (org.securityAlertEmail) {
      return emailValidator(org.securityAlertEmail);
    }
    if (!ownerNotifyEnabled(org)) {
      return REQUIRED_WHEN_OWNER_NOTIFY_DISABLED;
    }
    return undefined;
  },
  opsEmail: (org: Organization) => {
    if (org.opsAlertEmail) {
      return emailValidator(org.opsAlertEmail);
    }
    if (!ownerNotifyEnabled(org)) {
      return REQUIRED_WHEN_OWNER_NOTIFY_DISABLED;
    }
    return undefined;
  },
  name: (org: Organization) => existValidator(org.name, "name"),
};

const DELETE_REASONS = [
  "Costs are too high",
  "Migrated to self-hosted AWS",
  "Migrated to self-hosted GCP or Azure",
  "Migrated to alternative PaaS",
  "Sunset/shut down application",
  "Signed up by mistake",
  "Other",
];

const AccountDeletion = ({ org }: { org: Organization }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [reasons, setReasons] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [confirm, setConfirm] = useState("");

  const currentUser = useSelector(selectCurrentUser);
  const loader = useLoader(createSupportTicket);

  // Reset loader state on unmount so success state does not persist across pages
  useEffect(() => {
    return () => {
      dispatch(resetSupportTicket());
    };
  }, []);

  // Fetch cost data for stacks
  useQuery(fetchCostsByStacks({ orgId: org.id }));
  const costs = useSelector(schema.costs.selectTable);

  // Get stacks for this organization
  const stacks = useSelector(selectStacksByOrgAsList);

  // Get stacks with costs > $0 -- these block account deletion
  const stacksWithCosts = stacks.filter((stack) => {
    const costItem = schema.costs.findById(costs, {
      id: computeCostId("Stack", stack.id),
    });
    return costItem.estCost > 0;
  });
  const canDeleteAccount = stacksWithCosts.length === 0;

  const resetForm = () => {
    setReasons([]);
    setMessage("");
    setConfirm("");
  };

  const openModal = () => {
    resetForm();
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  useLoaderSuccess(loader, () => {
    resetForm();
    closeModal();
  });

  const toggleReason = (reason: string) => {
    setReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason],
    );
  };

  const invalid =
    reasons.length === 0 || !message.trim() || confirm !== org.name;

  const onSubmit = () => {
    if (invalid || !canDeleteAccount || loader.isLoading) return;

    const subject = "Account Deletion Request";
    let description = `<p>Your request has been received by the Aptible Support team. If you'd like to cancel this request, reply here to let us know.</p>`;
    description += "<p>--</p>";
    description += `<p><strong>User requested account deletion for organization:</strong> ${org.name} (ID: ${org.id})</p>`;
    description += `<p><strong>User:</strong> ${currentUser.name} (${currentUser.email})<br/>`;
    description += `<strong>Organization ID:</strong> ${org.id}<br/>`;
    description += `<strong>Organization Name:</strong> ${org.name}</p>`;

    description += `<p><strong>Reasons for deleting account:</strong><br/>${reasons.join("<br/>")}</p>`;
    description += `<p><strong>Additional details:</strong><br/>${message.trim().replace(/\n/g, "<br/>")}</p>`;

    description +=
      "<p><strong>Status:</strong> No billable resources are currently running.</p>";

    const action = createSupportTicket({
      subject,
      description,
      email: currentUser.email,
      name: currentUser.name,
      priority: "normal",
      attachments: [],
    });

    dispatch(action);
  };

  return (
    <>
      <Box className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Delete Account
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Permanently delete this organization and all of its data.
          </p>
        </div>
        <Button
          type="button"
          variant="delete"
          onClick={openModal}
          className="shrink-0"
        >
          Delete Account
        </Button>
      </Box>

      {isOpen ? (
        <ModalWrapper>
          <Modal isOpen={isOpen} onClose={closeModal} className="min-w-[550px]">
            <ModalHeading title="Delete account" onClose={closeModal} />
            <ModalBody>
              {canDeleteAccount ? (
                <div className="flex flex-col gap-5">
                  <p className="text-gray-700">
                    Your organization <strong>{org.name}</strong> will be
                    permanently deleted once this process is complete. This
                    cannot be undone.
                  </p>

                  <div className="flex flex-col gap-1">
                    <label className={tokens.type.h4}>
                      Reasons for deleting account (required)
                      <span className="ml-1 font-normal text-gray-500">
                        &mdash; select all that apply
                      </span>
                    </label>
                    <div className="flex flex-col gap-2 mt-1">
                      {DELETE_REASONS.map((reason) => (
                        <CheckBox
                          key={reason}
                          label={reason}
                          labelClassName="ml-3"
                          checked={reasons.includes(reason)}
                          onChange={() => toggleReason(reason)}
                        />
                      ))}
                    </div>
                  </div>

                  <FormGroup
                    label="Tell us more (required)"
                    htmlFor="cancellation-message"
                  >
                    <TextArea
                      id="cancellation-message"
                      name="cancellation-message"
                      placeholder="Please share more detail about why you're deleting this account"
                      value={message}
                      onChange={(e) => setMessage(e.currentTarget.value)}
                    />
                  </FormGroup>

                  <FormGroup
                    label={`Type "${org.name}" to confirm (required)`}
                    htmlFor="delete-confirm"
                  >
                    <Input
                      id="delete-confirm"
                      name="delete-confirm"
                      type="text"
                      value={confirm}
                      onChange={(e) => setConfirm(e.currentTarget.value)}
                      disabled={loader.isLoading}
                    />
                  </FormGroup>

                  <BannerMessages {...loader} />
                </div>
              ) : (
                <div className="text-gray-700">
                  You must first deprovision all existing running resources
                  (Environments, Apps, Databases, Endpoints, Backups, etc.) in
                  your <strong>{org.name}</strong> organization. The following
                  Stacks still have actively running resources:
                  <ul className="mt-2 ml-4 list-disc">
                    {stacksWithCosts.map((stack) => (
                      <li key={stack.id}>
                        <Link
                          to={stackDetailEnvsUrl(stack.id)}
                          className="text-indigo-600 hover:text-indigo-800 underline"
                        >
                          {stack.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </ModalBody>
            <ModalFooter
              actions={
                canDeleteAccount
                  ? [
                      <Button key="cancel" variant="white" onClick={closeModal}>
                        Cancel
                      </Button>,
                      <Button
                        key="submit"
                        variant="delete"
                        disabled={invalid || loader.isLoading}
                        isLoading={loader.isLoading}
                        onClick={onSubmit}
                      >
                        Submit Deletion Request
                      </Button>,
                    ]
                  : [
                      <Button key="close" variant="white" onClick={closeModal}>
                        Close
                      </Button>,
                    ]
              }
            />
          </Modal>
        </ModalWrapper>
      ) : null}
    </>
  );
};

export const TeamContactsPage = () => {
  const dispatch = useDispatch();
  const org = useSelector(selectOrganizationSelected);
  const [orgData, setOrgData] = useState(org);
  const updateOrgData = <K extends keyof Organization>(
    key: K,
    value: Organization[K],
  ) => {
    setOrgData({
      ...orgData,
      [key]: value,
    });
  };
  const action = updateOrganization(orgData);
  const loader = useLoader(action);
  const [errors, validate] = useValidator<Organization, typeof validators>(
    validators,
  );
  const ownerNotify = ownerNotifyEnabled(orgData);
  const setOwnerNotify = (checked: boolean) => {
    setOrgData({
      ...orgData,
      opsAlertOwnerNotify: checked,
      securityAlertOwnerNotify: checked,
    });
  };
  const requiredEmailsIncomplete =
    !ownerNotify && (!orgData.opsAlertEmail || !orgData.securityAlertEmail);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate(orgData)) return;
    dispatch(action);
  };
  const onCancel = () => {
    setOrgData(org);
  };

  useEffect(() => {
    setOrgData(org);
  }, [org.id, org.name]);

  return (
    <Group>
      <h2 className={tokens.type.h2}>Organization Settings</h2>
      <Box>
        <Group>
          <form onSubmit={onSubmit}>
            <Group>
              <BannerMessages {...loader} />

              <FormGroup label="Organization Name" htmlFor="org-name">
                <Input
                  id="org-name"
                  name="org-name"
                  value={orgData.name}
                  onChange={(e) => updateOrgData("name", e.currentTarget.value)}
                />
              </FormGroup>

              <div className="pt-2">
                <h3 className={tokens.type.h4}>Organization Notifications</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Manage organization notification settings for security &
                  operational alert emails. Operational alerts, aka "Ops
                  Alerts," often include notifications regarding maintenance,
                  reliability, and other operational events.{" "}
                  <a
                    href={RELIABILITY_DOCS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 underline"
                  >
                    See docs on reliability for more information.
                  </a>
                </p>
              </div>

              <div className="border-t border-black-100 pt-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Default Notifications
                </h4>
                <div className="mt-3 flex items-start justify-between gap-4">
                  <div>
                    <div className="text-base font-semibold text-black">
                      Notify all Account Owners on Security & Ops Alert emails
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      When enabled, every account owner on this organization
                      automatically receives a copy of all security and ops
                      alert emails.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className={
                        ownerNotify
                          ? "text-forest text-sm font-medium"
                          : "text-gray-500 text-sm font-medium"
                      }
                    >
                      {ownerNotify ? "Enabled" : "Disabled"}
                    </span>
                    <Switch
                      ariaLabel="Notify all Account Owners on Security & Ops Alert emails"
                      checked={ownerNotify}
                      onChange={setOwnerNotify}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-black-100 pt-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Additional Notification Emails
                </h4>
                <div className="mt-3 bg-gray-100 border border-gray-200 rounded-md px-4 py-3 flex items-start gap-3">
                  <IconInfo
                    variant="sm"
                    className="mt-0.5 flex-shrink-0 text-gray-500"
                  />
                  <div className="text-sm text-gray-800">
                    <strong>Designed for distribution groups</strong> (e.g.{" "}
                    engineers@healthco.com, security@healthco.com). Not
                    recommended for individual/personal email. Optional, unless{" "}
                    Notify all Account Owners on Security & Ops Alert emails
                    above is disabled, in which case both are required.
                  </div>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row gap-4">
                  <FormGroup
                    label={
                      <span className="text-base font-semibold text-black">
                        Ops Alert Notification Email
                        {!ownerNotify && (
                          <span className="text-red-500"> *</span>
                        )}
                      </span>
                    }
                    htmlFor="ops-alert-email"
                    className="flex-1"
                    feedbackMessage={errors.opsEmail}
                    feedbackVariant={errors.opsEmail ? "danger" : "info"}
                  >
                    <Input
                      id="ops-alert-email"
                      name="ops-alert-email"
                      placeholder="Leave blank to notify account owners instead"
                      value={orgData.opsAlertEmail}
                      onChange={(e) =>
                        updateOrgData("opsAlertEmail", e.currentTarget.value)
                      }
                    />
                  </FormGroup>

                  <FormGroup
                    label={
                      <span className="text-base font-semibold text-black">
                        Security Alert Notification Email
                        {!ownerNotify && (
                          <span className="text-red-500"> *</span>
                        )}
                      </span>
                    }
                    htmlFor="security-alert-email"
                    className="flex-1"
                    feedbackMessage={errors.securityEmail}
                    feedbackVariant={errors.securityEmail ? "danger" : "info"}
                  >
                    <Input
                      id="security-alert-email"
                      name="security-alert-email"
                      placeholder="Leave blank to notify account owners instead"
                      value={orgData.securityAlertEmail}
                      onChange={(e) =>
                        updateOrgData(
                          "securityAlertEmail",
                          e.currentTarget.value,
                        )
                      }
                    />
                  </FormGroup>
                </div>
              </div>

              <div className="border-t border-black-100 pt-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Phone Numbers
                </h4>

                <div className="mt-4 flex flex-col sm:flex-row gap-4">
                  <FormGroup
                    label={
                      <span className="text-base font-semibold text-black">
                        Primary Phone
                      </span>
                    }
                    htmlFor="phone"
                    className="flex-1"
                  >
                    <Input
                      id="phone"
                      name="phone"
                      value={orgData.primaryPhone}
                      onChange={(e) =>
                        updateOrgData("primaryPhone", e.currentTarget.value)
                      }
                    />
                  </FormGroup>

                  <FormGroup
                    label={
                      <span className="text-base font-semibold text-black">
                        Emergency Phone
                      </span>
                    }
                    htmlFor="emergency-phone"
                    className="flex-1"
                  >
                    <Input
                      id="emergency-phone"
                      name="emergency-phone"
                      value={orgData.emergencyPhone}
                      onChange={(e) =>
                        updateOrgData("emergencyPhone", e.currentTarget.value)
                      }
                    />
                  </FormGroup>
                </div>
              </div>

              <Group variant="horizontal" size="sm">
                {requiredEmailsIncomplete ? (
                  <Tooltip text="Must complete required fields">
                    <Button type="button" disabled>
                      Save Changes
                    </Button>
                  </Tooltip>
                ) : (
                  <Button type="submit" isLoading={loader.isLoading}>
                    Save Changes
                  </Button>
                )}
                <Button
                  variant="white"
                  onClick={onCancel}
                  disabled={loader.isLoading}
                >
                  Cancel
                </Button>
              </Group>
            </Group>
          </form>
        </Group>
      </Box>

      <AccountDeletion org={org} />
    </Group>
  );
};
